import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLists } from '../contexts/ListsContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlaceStatusCache } from '../contexts/PlaceStatusCacheContext';
import { useLocation } from '../contexts/LocationContext';
import api from '../services/api';
import { getSubcategoryFromType } from '../services/googleMaps';
import { trackListView } from '../services/analytics';
import { isOpenDuringAny } from '../utils/openingHoursUtils';

// Cache for external list data. Fresh entries (< 5 min) skip the refetch;
// stale entries still render instantly while a background refetch runs
// (stale-while-revalidate). Entries persist to sessionStorage so repeat
// visits after a page refresh render without a spinner.
const CACHE_TTL = 5 * 60 * 1000;
const CACHE_KEY_PREFIX = 'vibary_list_detail_v1_';
const listDetailCache = new Map(); // listId -> { list, timestamp }

const readCacheEntry = (listId) => {
  const mem = listDetailCache.get(listId);
  if (mem) {
    return mem;
  }
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + listId);
    if (raw) {
      const entry = JSON.parse(raw);
      listDetailCache.set(listId, entry);
      return entry;
    }
  } catch {
    // Ignore parse/storage errors
  }
  return null;
};

const getCachedList = (listId, { allowStale = false } = {}) => {
  const entry = readCacheEntry(listId);
  if (!entry) {
    return null;
  }
  if (allowStale || (Date.now() - entry.timestamp) < CACHE_TTL) {
    return entry.list;
  }
  return null;
};

const setCachedList = (listId, list) => {
  const entry = { list, timestamp: Date.now() };
  listDetailCache.set(listId, entry);
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + listId, JSON.stringify(entry));
  } catch {
    // Storage full (very large lists) - the in-memory cache still applies
  }
};

// Export for cache invalidation when list is modified
export const invalidateListCache = (listId) => {
  listDetailCache.delete(listId);
  try {
    sessionStorage.removeItem(CACHE_KEY_PREFIX + listId);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Custom hook for fetching and managing list detail data
 */
export function useListDetail(listId) {
  const { user, authReady } = useAuth();
  const { getListById, fetchLists, updateListLocally } = useLists();
  const { batchGetOpeningHours, getOpenStatusInfo } = usePlaceStatusCache();

  // First try to get list from local context (user's own lists)
  // This is called early so we can use it to initialize loadingList
  const localList = getListById(listId);

  // Check for cached external list (stale entries render too; the fetch
  // effect revalidates them in the background)
  const cachedList = !localList ? getCachedList(listId, { allowStale: true }) : null;

  // External list state - initialize from cache if available
  const [externalList, setExternalList] = useState(cachedList);
  // Only show loading if we don't already have the list locally or cached
  const [loadingList, setLoadingList] = useState(!localList && !cachedList);
  const [listError, setListError] = useState(null);

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [notificationsMuted, setNotificationsMuted] = useState(false);
  const [notificationToggleLoading, setNotificationToggleLoading] = useState(false);
  const [localFollowersCount, setLocalFollowersCount] = useState(null);

  // User places map (for external lists)
  const [userPlacesMap, setUserPlacesMap] = useState({});
  const [friendsPlacesMap, setFriendsPlacesMap] = useState({});

  // Place statuses
  const [placeStatuses, setPlaceStatuses] = useState({});

  // Two-phase loading state: total item count and full placeId list arrive
  // with the first page's pagination metadata, remaining pages stream in
  const [loadingMoreItems, setLoadingMoreItems] = useState(false);
  const [paginationTotal, setPaginationTotal] = useState(null);
  const [allPlaceIds, setAllPlaceIds] = useState(null);

  // Use local list if available, otherwise use fetched external list
  const list = localList || externalList;
  const isOwnList = list && user && (list.userId === user.userId || list.userId === user.id);
  const places = useMemo(() => list?.items || [], [list?.items]);

  // Track if we've fetched enriched data for this list to avoid re-fetching
  const enrichedFetchedRef = useRef(false);
  const lastFetchedListIdRef = useRef(null);
  // Track if we have full place data (not heroPhotoOnly) for opening hours check
  // Initialize to true if we have cached data (which comes from api.getList with full data)
  const [hasFullPlaceData, setHasFullPlaceData] = useState(!!cachedList);
  // Store stable references to avoid dependency issues
  const updateListLocallyRef = useRef(updateListLocally);
  updateListLocallyRef.current = updateListLocally;

  // Reset fetch tracking when listId changes and update local list ref
  useEffect(() => {
    if (lastFetchedListIdRef.current !== listId) {
      enrichedFetchedRef.current = false;
      lastFetchedListIdRef.current = listId;
      setHasFullPlaceData(false);
      setLoadingMoreItems(false);
      setPaginationTotal(null);
      setAllPlaceIds(null);
    }
  }, [listId]);

  // Track whether we have a local list (updated synchronously before useEffect runs)
  const hasLocalListRef = useRef(!!localList);
  hasLocalListRef.current = !!localList;

  // Fetch list data from API
  // For own lists: fetch enriched data in background and update context (for photoUrls, etc.)
  // For external lists: fetch and store in local state (with caching)
  useEffect(() => {
    // Use the ref value to check if local list exists
    const hasLocalList = hasLocalListRef.current;

    // Skip if we've already fetched enriched data for own lists
    if (hasLocalList && enrichedFetchedRef.current) {
      setLoadingList(false);
      return;
    }

    // For external lists, check for cached data. Fresh cache short-circuits
    // the fetch entirely; stale cache renders immediately while the fetch
    // below revalidates in the background (stale-while-revalidate).
    let staleCachedList = null;
    if (!hasLocalList) {
      const freshCached = getCachedList(listId);
      if (freshCached) {
        setExternalList(freshCached);
        setLoadingList(false);
        // Cached data from api.getList has full place data
        setHasFullPlaceData(true);
        return;
      }
      staleCachedList = getCachedList(listId, { allowStale: true });
      if (staleCachedList) {
        setExternalList(staleCachedList);
        setLoadingList(false);
        setHasFullPlaceData(true);
      }
    }

    // Wait for Firebase auth to be ready before making API calls
    // This prevents 401 errors when the page is refreshed and the cached user
    // exists but Firebase hasn't restored the session yet
    if (!authReady) {
      return;
    }

    let cancelled = false;

    // Two-phase load: a small first page renders immediately, then the
    // remaining pages stream in. Large lists no longer block first paint on
    // the full enriched payload.
    const FIRST_PAGE_SIZE = 30;
    const PAGE_SIZE = 300;

    const fetchListData = async () => {
      // If we already rendered something (own list from context, or stale
      // cached external list), keep it visible while fetching fresh data
      if (hasLocalList || staleCachedList) {
        setLoadingList(false);
      } else {
        setLoadingList(true);
        setListError(null);
      }

      // Rows beyond the streamed prefix stay visible while pages arrive:
      // own lists use the context rows (hero-photo quality), external lists
      // with a stale cached render keep the stale rows until replaced
      const baseContextItems = hasLocalList
        ? (getListById(listId)?.items || [])
        : (staleCachedList?.items || []);

      // Opening hours for a just-arrived page: items are fully enriched, so
      // this resolves from inline data without triggering per-place API calls
      const loadPageHours = (pageItems) => {
        const withIds = pageItems.filter((p) => p.placeId);
        if (withIds.length === 0) return;
        batchGetOpeningHours(withIds)
          .then((statusMap) => {
            if (!cancelled) {
              setPlaceStatuses((prev) => ({ ...prev, ...statusMap }));
            }
          })
          .catch(() => {});
      };

      let renderedAny = false;

      try {
        const first = await api.getList(listId, { limit: FIRST_PAGE_SIZE, offset: 0 });
        if (cancelled || !first.data) return;
        renderedAny = true;

        const shell = first.data;
        const pagination = shell.itemsPagination;
        const { itemsPagination, ...listFields } = shell;

        const applyItems = (serverItems, complete) => {
          const items = !complete && baseContextItems.length > serverItems.length
            ? serverItems.concat(baseContextItems.slice(serverItems.length))
            : serverItems;
          const mergedList = { ...listFields, items };
          if (hasLocalList) {
            updateListLocallyRef.current(listId, mergedList);
          } else {
            setExternalList(mergedList);
            if (complete) {
              // Only cache complete lists so cached renders are never partial
              setCachedList(listId, mergedList);
            }
          }
          if (complete) {
            enrichedFetchedRef.current = true;
            setHasFullPlaceData(true);
            setLoadingMoreItems(false);
          }
        };

        if (!pagination) {
          // Full payload (no pagination metadata) - legacy response shape
          applyItems(shell.items || [], true);
          return;
        }

        setPaginationTotal(pagination.totalItems);
        if (pagination.allPlaceIds) {
          setAllPlaceIds(pagination.allPlaceIds);
        }

        let serverItems = shell.items || [];
        if (!pagination.hasMore) {
          applyItems(serverItems, true);
          return;
        }

        setLoadingMoreItems(true);
        applyItems(serverItems, false);
        loadPageHours(serverItems);

        while (serverItems.length < pagination.totalItems) {
          const page = await api.getList(listId, { limit: PAGE_SIZE, offset: serverItems.length });
          if (cancelled) return;
          const pageItems = page.data?.items || [];
          if (pageItems.length === 0) {
            // List shrank server-side mid-stream; stop and settle with what we have
            break;
          }
          const total = page.data?.itemsPagination?.totalItems ?? pagination.totalItems;
          serverItems = serverItems.concat(pageItems);
          applyItems(serverItems, serverItems.length >= total);
          loadPageHours(pageItems);
        }

        if (!cancelled) {
          // Settle flags even if the loop exited early
          enrichedFetchedRef.current = true;
          setHasFullPlaceData(true);
          setLoadingMoreItems(false);
        }
      } catch (error) {
        console.error('Failed to fetch list:', error);
        if (!cancelled) {
          setLoadingMoreItems(false);
          // Only surface an error state if nothing rendered yet - a failed
          // page fetch after the shell (or a stale cached render) should not
          // wipe visible content
          if (!hasLocalList && !renderedAny && !staleCachedList) {
            setListError(error.message || 'Failed to load list');
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingList(false);
        }
      }
    };

    fetchListData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, authReady]);

  // Check if user is following this list (for external lists)
  useEffect(() => {
    if (!isOwnList && list && user && authReady) {
      const checkFollowStatus = async () => {
        try {
          const response = await api.isFollowingList(listId);
          setIsFollowing(response.data?.isFollowing || false);
          setNotificationsMuted(response.data?.notificationsMuted || false);
        } catch (error) {
          console.error('Failed to check follow status:', error);
        }
      };
      checkFollowStatus();
    }
  }, [listId, isOwnList, list, user, authReady]);

  // Initialize local followers count and share token from list
  useEffect(() => {
    if (list) {
      setLocalFollowersCount(list.followersCount || 0);
    }
  }, [list]);

  // Track list view analytics (only for external lists, not own lists)
  // Use a ref to track if we've already tracked this list view in this session
  const trackedListViewRef = useRef(null);
  useEffect(() => {
    // Only track views for lists that aren't owned by the current user
    // The backend also filters out self-views, but we skip the call entirely here
    if (!list || !list.userId || isOwnList) return;

    // Don't re-track the same list in this component instance
    if (trackedListViewRef.current === listId) return;

    trackedListViewRef.current = listId;
    trackListView(listId, list.userId);
  }, [listId, list, isOwnList]);

  // Load user places and friends places (doesn't require full place data).
  // Prefer the complete placeId list from pagination metadata so streaming
  // pages cover the whole list in one fetch; key the effect on the joined
  // ID string so item enrichment (new object identities, same IDs) doesn't
  // refire the requests.
  const placeMapIds = useMemo(() => {
    if (allPlaceIds && allPlaceIds.length > 0) {
      return allPlaceIds;
    }
    return places.filter(p => p.placeId).map(p => p.placeId);
  }, [allPlaceIds, places]);
  const placeMapIdsKey = placeMapIds.join(',');

  useEffect(() => {
    if (placeMapIds.length === 0 || !user || !authReady) return;

    const loadPlaceMaps = async () => {
      try {
        const [userPlacesResponse, friendsPlacesResponse] = await Promise.all([
          api.getUserPlacesByPlaceIds(placeMapIds),
          api.getFriendsPlaces(placeMapIds),
        ]);
        setUserPlacesMap(userPlacesResponse.data || {});
        setFriendsPlacesMap(friendsPlacesResponse.data || {});
      } catch (error) {
        console.error('Failed to load place maps:', error);
      }
    };

    loadPlaceMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeMapIdsKey, user, authReady]);

  // Load opening hours ONLY after we have full place data (not heroPhotoOnly)
  // This prevents excessive API calls when ListsContext returns minimal data
  useEffect(() => {
    if (places.length === 0 || !user || !authReady || !hasFullPlaceData) return;

    const placesWithData = places.filter(p => p.placeId);
    if (placesWithData.length === 0) return;

    const loadOpeningHours = async () => {
      try {
        const statusMap = await batchGetOpeningHours(placesWithData);
        setPlaceStatuses(statusMap);
      } catch (error) {
        console.error('Failed to load opening hours:', error);
      }
    };

    loadOpeningHours();
  }, [places, user, authReady, hasFullPlaceData, batchGetOpeningHours]);

  // Helper to check if a place is currently open
  const isPlaceOpen = useCallback((place) => {
    if (!place.placeId || !placeStatuses[place.placeId]) {
      return null;
    }
    return placeStatuses[place.placeId].openNow;
  }, [placeStatuses]);

  // Get open status indicator
  const getOpenStatusIndicator = useCallback((place) => {
    if (!place.placeId || !placeStatuses[place.placeId]) {
      return null;
    }

    const status = placeStatuses[place.placeId];

    if (status.statusInfo) {
      return status.statusInfo;
    }

    if (status.openingHours) {
      return getOpenStatusInfo(status.openingHours);
    }

    return null;
  }, [placeStatuses, getOpenStatusInfo]);

  // Refresh user places map for a specific place
  const refreshUserPlacesMap = useCallback(async (placeId) => {
    if (!placeId) return;
    try {
      const response = await api.getUserPlacesByPlaceIds([placeId]);
      setUserPlacesMap(prev => ({
        ...prev,
        [placeId]: response.data?.[placeId] || [],
      }));
    } catch (error) {
      console.error('Failed to refresh place data:', error);
    }
  }, []);

  // Re-fetch the full list data (call after modifications like import)
  const refetchList = useCallback(async () => {
    try {
      const response = await api.getList(listId);
      if (response.data) {
        if (localList) {
          // For own lists: update context with enriched data
          updateListLocallyRef.current(listId, response.data);
        } else {
          // For external lists: update local state and cache
          setExternalList(response.data);
          setCachedList(listId, response.data);
        }
        setHasFullPlaceData(true);
      }
    } catch (error) {
      console.error('Failed to refetch list:', error);
    }
  }, [listId, localList]);

  return {
    // List data
    list,
    places,
    isOwnList,
    loadingList,
    listError,
    hasFullPlaceData,
    // Two-phase loading: true while remaining item pages stream in;
    // totalPlaceCount is stable from the first response
    loadingMoreItems,
    totalPlaceCount: paginationTotal ?? places.length,

    // Follow state
    isFollowing,
    setIsFollowing,
    followLoading,
    setFollowLoading,
    notificationsMuted,
    setNotificationsMuted,
    notificationToggleLoading,
    setNotificationToggleLoading,
    localFollowersCount,
    setLocalFollowersCount,

    // Places maps
    userPlacesMap,
    friendsPlacesMap,
    placeStatuses,
    refreshUserPlacesMap,

    // Helpers
    isPlaceOpen,
    getOpenStatusIndicator,

    // Context functions
    fetchLists,
    refetchList,
    user,
  };
}

/**
 * Custom hook for user location
 * Uses LocationContext which handles the pre-prompt modal
 */
export function useUserLocation() {
  const { userLocation, requestLocation } = useLocation();

  // Request location on mount (will show pre-prompt if needed)
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  return userLocation;
}

/**
 * Custom hook for touch device detection
 */
export function useTouchDevice() {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      const hasTouch = 'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches;
      setIsTouchDevice(hasTouch);
    };

    checkTouchDevice();

    const handleFirstTouch = () => {
      setIsTouchDevice(true);
      window.removeEventListener('touchstart', handleFirstTouch);
    };
    window.addEventListener('touchstart', handleFirstTouch);

    return () => {
      window.removeEventListener('touchstart', handleFirstTouch);
    };
  }, []);

  return isTouchDevice;
}

/**
 * Custom hook for places with distances
 */
export function usePlacesWithDistances(places, userLocation) {
  return useMemo(() => {
    if (!userLocation) return places.map(p => ({ ...p, distance: null }));

    const getUnitPreference = () => {
      try {
        const prefs = localStorage.getItem('vibary_preferences');
        if (prefs) {
          const parsed = JSON.parse(prefs);
          return parsed.units || 'imperial';
        }
      } catch (e) {
        console.error('Failed to parse preferences:', e);
      }
      return 'imperial';
    };

    const calculateDistance = (lat1, lon1, lat2, lon2, useMetric = false) => {
      const R = useMetric ? 6371 : 3959;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    };

    const useMetric = getUnitPreference() === 'metric';
    const unit = useMetric ? 'km' : 'mi';

    const formatDistance = (distance) => {
      if (distance < 10) {
        return distance.toFixed(1);
      } else if (distance >= 1000) {
        return Math.round(distance).toLocaleString();
      } else {
        return Math.round(distance).toString();
      }
    };

    return places.map(place => {
      if (!place.lat || !place.lng) {
        return { ...place, distance: null };
      }
      const distance = calculateDistance(userLocation.lat, userLocation.lng, place.lat, place.lng, useMetric);
      return { ...place, distance: `${formatDistance(distance)} ${unit}` };
    });
  }, [places, userLocation]);
}

/**
 * Custom hook for filtered places
 * @param {Object} options
 * @param {Function} options.getDesignation - Function to get user's designation for a placeId
 * @param {Object} options.openAtFilter - { days: number[], slots: string[] } for time-based filtering
 * @param {Object} options.placeStatuses - Map of placeId to opening hours data
 */
export function useFilteredPlaces({
  placesWithDistances,
  activeTab,
  searchQuery,
  sortOption,
  userLocation,
  userPlacesMap,
  isPlaceOpen,
  getDesignation,
  isOwnList = true,
  isUserCollaborator = false,
  openAtFilter = null,
  placeStatuses = {},
}) {
  return useMemo(() => {
    let filtered = placesWithDistances;

    // Determine if we should use owner's designation for sorting/filtering
    // - Own lists: use user's designation
    // - Collaboration lists where user is collaborator: use user's designation
    // - Other users' lists (followed/viewed): use owner's designation
    const shouldUseOwnerDesignation = !isOwnList && !isUserCollaborator;

    // Helper to get badge for filtering/sorting
    const getBadge = (place) => {
      if (!place.placeId) return null;
      if (shouldUseOwnerDesignation) {
        // Use owner's designation from the place data (set by backend)
        return place.ownerDesignation?.badge || place.badge || null;
      }
      // Use user's global designation
      if (!getDesignation) return null;
      const designation = getDesignation(place.placeId);
      return designation?.badge || null;
    };

    // Helper to get designation for search (notes, etc.)
    const getDesignationForSearch = (place) => {
      if (!place.placeId) return null;
      if (shouldUseOwnerDesignation) {
        return place.ownerDesignation || null;
      }
      return getDesignation ? getDesignation(place.placeId) : null;
    };

    // Filter by tab
    if (activeTab === 'been') {
      filtered = filtered.filter(place => {
        const badge = getBadge(place);
        return badge === 'visited' ||
          badge === 'gem' ||
          badge === 'highly-recommended' ||
          badge === 'avoid';
      });
    } else if (activeTab === 'want-to-go') {
      filtered = filtered.filter(place => getBadge(place) === 'want-to-go');
    } else if (activeTab === 'listed') {
      filtered = filtered.filter(place => {
        const placeInfo = userPlacesMap[place.placeId];
        return placeInfo && placeInfo.length > 0;
      });
    } else if (activeTab === 'unlisted') {
      filtered = filtered.filter(place => {
        const placeInfo = userPlacesMap[place.placeId];
        return !placeInfo || placeInfo.length === 0;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(place => {
        const badge = getBadge(place);
        const designation = getDesignationForSearch(place);
        const badgeLabel = badge === 'gem' ? 'Gem' :
                          badge === 'highly-recommended' ? 'Highly Recommended' :
                          badge === 'visited' ? 'Visited' :
                          badge === 'avoid' ? 'Avoid' :
                          badge === 'want-to-go' ? 'Want to Go' : '';

        // Get subcategory from place type (e.g., "Italian", "Sushi", "Coffee")
        const subcategory = getSubcategoryFromType(place.placeType);

        return (
          place.name?.toLowerCase().includes(searchLower) ||
          place.address?.toLowerCase().includes(searchLower) ||
          place.category?.toLowerCase().includes(searchLower) ||
          designation?.notes?.toLowerCase().includes(searchLower) ||
          badgeLabel.toLowerCase().includes(searchLower) ||
          subcategory?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter to only open places if sortOption is 'open'
    if (sortOption === 'open') {
      filtered = filtered.filter(place => isPlaceOpen(place) === true);
    }

    // Filter by Open At (day + time slot combinations)
    // Places without opening hours data go to the bottom
    const isOpenAtActive = openAtFilter && openAtFilter.days?.length > 0 && openAtFilter.slots?.length > 0;
    let placesWithoutHours = [];

    if (isOpenAtActive) {
      const { days, slots } = openAtFilter;
      const placesWithHours = [];

      for (const place of filtered) {
        const status = placeStatuses[place.placeId];
        const openingHours = status?.openingHours;

        if (!openingHours?.periods) {
          // No opening hours data - save for bottom
          placesWithoutHours.push(place);
        } else {
          // Check if open during any of the selected day+slot combinations
          const isMatch = isOpenDuringAny(openingHours, days, slots);
          if (isMatch === true) {
            placesWithHours.push(place);
          }
          // If false or null, exclude from results (but null places went to placesWithoutHours)
        }
      }

      // Matching places first, then places without hours at bottom
      filtered = placesWithHours;
    }

    // Sort based on selected option
    if (sortOption === 'nearest' && userLocation) {
      filtered = [...filtered].sort((a, b) => {
        if (!a.distance && !b.distance) return 0;
        if (!a.distance) return 1;
        if (!b.distance) return -1;
        const distA = parseFloat(a.distance);
        const distB = parseFloat(b.distance);
        return distA - distB;
      });
    } else if (sortOption === 'category') {
      filtered = [...filtered].sort((a, b) => {
        const catA = a.category || 'zzz';
        const catB = b.category || 'zzz';
        return catA.localeCompare(catB);
      });
    } else if (sortOption === 'default' || sortOption === 'open') {
      if (activeTab !== 'want-to-go') {
        const badgePriority = {
          'gem': 1,
          'highly-recommended': 2,
          'visited': 3,
          'avoid': 4,
          'want-to-go': 5
        };

        filtered = [...filtered].sort((a, b) => {
          const priorityA = badgePriority[getBadge(a)] || 999;
          const priorityB = badgePriority[getBadge(b)] || 999;
          return priorityA - priorityB;
        });
      }
    }

    // Append places without hours at the bottom when Open At filter is active
    if (isOpenAtActive && placesWithoutHours.length > 0) {
      // Mark these places so UI can show "Hours unknown" indicator
      placesWithoutHours = placesWithoutHours.map(p => ({ ...p, _hoursUnknown: true }));
      filtered = [...filtered, ...placesWithoutHours];
    }

    return filtered;
  }, [placesWithDistances, activeTab, searchQuery, sortOption, isPlaceOpen, userLocation, userPlacesMap, getDesignation, isOwnList, isUserCollaborator, openAtFilter, placeStatuses]);
}
